import { AnimatePresence, m } from 'framer-motion';
// @mui
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
// utils
import { bytesToMB } from '../../utils/format-number';
//
import { varFade } from '../animate';
import { FileThumbnail } from '../file-thumbnail';
import { Iconify } from '../iconify';
//
import { UploadProps } from './types';
import { Box, Button, Card, Divider, SvgIcon, Typography } from '@mui/material';
import { DeleteSvgIcon, DocumentExcelIcon, PDFIcon } from '../../assets/index';

// ----------------------------------------------------------------------

export default function MultiFilePreview({ thumbnail, files, onRemove, error, helperText, sx, onDelete, ...other }: UploadProps) {
  const theme = useTheme();

  return (
    <AnimatePresence initial={false}>
      {files?.map((file, index) => {
        if (thumbnail) {
          return (
            <Stack
              component={m.div}
              {...varFade().inUp}
              alignItems="center"
              display="inline-flex"
              justifyContent="center"
              sx={{
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.25,
                overflow: 'hidden',
                position: 'relative',
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                ...sx,
              }}
            >
              <FileThumbnail tooltip imageView file={file} sx={{ position: 'absolute' }} imgSx={{ position: 'absolute' }} />

              {onRemove && (
                <IconButton
                  size="small"
                  onClick={() => onRemove(file)}
                  sx={{
                    p: 0.5,
                    top: 4,
                    right: 4,
                    position: 'absolute',
                    color: 'common.white',
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    },
                  }}
                >
                  <Iconify icon="mingcute:close-line" width={14} />
                </IconButton>
              )}
            </Stack>
          );
        }

        return (
          <Card component={Box} px={2} py={2} gap={2} my={1} alignItems="start">
            <Stack direction={'row'} justifyContent="space-between" alignItems="center" {...other}>
              <Stack direction="row" gap={2}>
                <Box sx={{ borderRadius: 1, bgcolor: alpha(theme.palette.grey[500], 0.08), p: 12 / 8, height: 44, width: 44 }}>
                  {other?.showPdfIcon ? <PDFIcon /> : <DocumentExcelIcon />}
                </Box>

                <Stack>
                  <Typography variant="subtitle1" name="fileName">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" name="fileSize">
                    {bytesToMB(file.size)} MB
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {other?.showPreview && (
                  <Button variant="soft" color="primary" size="small" sx={{ mr: 1 }} onClick={other?.onPreview}>
                    Preview
                  </Button>
                )}
                <Divider orientation="vertical" variant="middle" flexItem />
                <IconButton onClick={() => onDelete?.(index)}>
                  <SvgIcon>
                    <DeleteSvgIcon />
                  </SvgIcon>
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        );
      })}
    </AnimatePresence>
  );
}
